import type { FieldError } from '@servicetim/shared';
import { ApiError } from '../../lib/api';

/** camelCase ticket properties → the snake_case keys the form uses. */
const CAMEL_TO_KEY: Record<string, string> = {
  priorityId: 'priority_id',
  assigneeId: 'assignee_id',
  reporterId: 'reporter_id',
  issueTypeId: 'issue_type_id',
  organizationId: 'organization_id',
  parentId: 'parent_id',
  projectId: 'project_id',
  dueAt: 'due_at',
  statusId: 'status_id',
};

/**
 * Maps a server `errors[].path` to a form field key:
 *
 *   fields.cf_abc12345      → cf_abc12345
 *   fields.cf_abc12345.0    → cf_abc12345
 *   cf_abc12345             → cf_abc12345
 *   summary / labels.2      → summary / labels
 *   priorityId              → priority_id
 *   comment.body            → comment.body   (left as is; unmapped unless the form has it)
 */
export function fieldNameFromErrorPath(path: string): string {
  const segments = path.split(/[.[\]]+/).filter((s) => s.length > 0);
  if (segments.length === 0) return path;
  let head = segments[0] as string;
  if (head === 'fields' && segments.length > 1) head = segments[1] as string;
  return CAMEL_TO_KEY[head] ?? head;
}

export type SetFieldError = (name: string, error: { type: string; message: string }) => void;

/**
 * Pushes problem+json field errors onto the form (`setError`) and returns the ones that do not
 * belong to any rendered field (shown in the banner instead). Non-`ApiError` values map nothing.
 */
export function applyServerErrors(
  error: unknown,
  setError: SetFieldError,
  knownKeys: ReadonlySet<string>,
): FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const unmapped: FieldError[] = [];
  for (const fieldError of error.fieldErrors) {
    const key = fieldNameFromErrorPath(fieldError.path);
    if (knownKeys.has(key)) {
      setError(key, { type: 'server', message: fieldError.message });
    } else {
      unmapped.push(fieldError);
    }
  }
  return unmapped;
}

/** Paths of the field errors that were mapped (to omit them from the banner). */
export function mappedErrorPaths(error: unknown, knownKeys: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  if (!(error instanceof ApiError)) return out;
  for (const f of error.fieldErrors) {
    if (knownKeys.has(fieldNameFromErrorPath(f.path))) out.add(f.path);
  }
  return out;
}
